/**
 * apps/web/src/api/queries.ts — the shared query-key factory.
 *
 * ONE file. No ad-hoc key arrays anywhere else in the app, ever. Every entry
 * names the endpoint id from `12-api-contract.md` so a reviewer can check the
 * wiring without guessing. Spec: 13-ui-screens.md §18.3.
 *
 * `qo()` is a thin wrapper over TanStack Query's `queryOptions`, so a route
 * loader (`queryClient.ensureQueryData(qk.sheets.list(f))`) and the component
 * (`useQuery(qk.sheets.list(f))`) share ONE object and the keys cannot drift.
 *
 * TanStack Query v5 reminders that bite this codebase:
 *   gcTime (not cacheTime) · isPending (not isLoading) · status 'pending' ·
 *   throwOnError (not useErrorBoundary) ·
 *   placeholderData: keepPreviousData — a VALUE, not `keepPreviousData: true` ·
 *   onSuccess/onError exist on useMutation ONLY, never on useQuery.
 */
import { queryOptions, useQuery } from '@tanstack/react-query';
import type {
  AbsencePenaltyRuleDto,
  AgentDto,
  AppConfigEntryDto,
  AuditEntryDto,
  CasualPeriodDto,
  CasualSlotDto,
  DaySheetDto,
  DocumentComplianceReportDto,
  DocumentExpiryDashboardDto,
  EngagementDto,
  DailyEarningsReportDto,
  ExceptionCodeDto,
  JobDayListItemDto,
  JobLineSearchResultDto,
  JobReportDto,
  JobMasterDto,
  JobMasterKind,
  JobDaySummaryDto,
  JobTicketDto,
  Paginated,
  AttendanceBoardDto,
  CanteenBoardDto,
  MedicalClaimDto,
  WorkerMedicalSummaryDto,
  WorkerLeaveBalancesDto,
  PayComponentTypeDto,
  PayPeriodDto,
  PayPeriodRefDto,
  PeriodBoundaryDto,
  PayrollLineListItemDto,
  PayrollRegisterDto,
  PayrollRunDto,
  PayrollRunListItemDto,
  PayslipDto,
  PayrollLineSourceDto,
  PayrollPolicyDto,
  PublicHolidayDto,
  PeriodReadinessDto,
  RateChangeRequestDto,
  RateCorrectionResultDto,
  RateHistoryEntryDto,
  RateKind,
  RateRepriceSummaryDto,
  ReconciliationBaselineDto,
  ReconciliationReportDto,
  RoleDto,
  RosterEntryDto,
  SetRateRequest,
  StatutoryRuleDto,
  TeamDto,
  TeamRefDto,
  TimeMultiplierDto,
  UnitOfMeasureDto,
  UserDto,
  VarianceReportDto,
  AvailableWorkerDto,
  WorkerDocumentDto,
  WorkerListItemDto,
  WorkerProfileDto,
  WorkTypeListItemDto,
  WorkTypeLookupItemDto,
  WorkTypeLookupQuery,
  DisbursementDto,
  ExportBatchDto,
  CurrentUserDto,
  PayingEntityDto,
  PageMeta,
} from '@cropmate/shared';
import { api, fetchBlob, type QueryParams } from './client';
import type { ExceptionCodeExtras, NavCountsDto } from './types';

/** A filter bag that is part of the cache key. Must be JSON-serialisable. */
export type Filters = QueryParams;

const MIN = 60_000;

function qo<T>(key: readonly unknown[], fn: () => Promise<T>, staleTime: number) {
  return queryOptions({ queryKey: key, queryFn: fn, staleTime });
}

/** The server's `MAX_PAGE_SIZE` (§1.5). Asking for more is a 400, not a bigger page. */
const MAX_PAGE_SIZE = 200;

/**
 * A guard, not a limit: 50 pages is 10 000 rows, far past a plant whose whole roll is a few
 * hundred. An unbounded walk is one API change away from spinning forever, and a list cut
 * short is the very bug `allPages` exists to end — so past the guard it THROWS (the screen
 * shows its error state) rather than quietly returning a prefix.
 */
const MAX_PAGES = 50;

export class ListTooLongError extends Error {
  constructor(
    readonly path: string,
    readonly total: number,
  ) {
    super(`${path} has ${String(total)} rows — more than one screen can load at once.`);
    this.name = 'ListTooLongError';
  }
}

/**
 * EVERY page of a §1.5 paginated list, merged into one response of the same shape — so a
 * screen that reads `data.items`, `data.page.total` or a sibling like `summary` needs no
 * other change.
 *
 * For lists a screen uses WHOLE: a dropdown, a facet, an id → name map, a client-side search.
 * Asking for one page there — the API's default 50, or its 200 ceiling — silently drops every
 * row past it. UAT, 7 Oct 2026, with 228 workers and 313 documents live: Medical Claims could
 * not pick 28 workers, the Documents page's MISSING list and team column stopped at row 200,
 * and the job sheet could not find a work type past the 30th (R2, fixed server-side). A table
 * with its own page controls should keep `list` and ask for one page at a time.
 *
 * Pages 2…n are fetched in parallel once page 1 has said how many there are. Each page is a
 * separate request, so a row written mid-walk can appear twice or not at all; for the lists
 * this serves — masters and rolls a clerk is choosing from — the next refetch settles it.
 * `payroll/-adjustments/queries.ts`'s `walkPages` predates this and keeps its own
 * screen-specific errors.
 */
export async function allPages<R extends { items: unknown[]; page: PageMeta }>(
  path: string,
  f: Filters,
): Promise<R> {
  const at = (page: number) => api.get<R>(path, { ...f, page, pageSize: MAX_PAGE_SIZE });
  const first = await at(1);
  const pages = first.page.totalPages;
  if (pages > MAX_PAGES) throw new ListTooLongError(path, first.page.total);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, i) => at(i + 2)),
  );
  const items = [first, ...rest].flatMap((r) => r.items) as R['items'];
  return {
    ...first,
    items,
    page: {
      page: 1,
      pageSize: items.length,
      total: first.page.total,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
    },
  };
}

/** F8 returns `severity`, which the shared DTO does not declare (see api/types.ts). */
export type ExceptionCodeRow = ExceptionCodeDto & ExceptionCodeExtras;

export const qk = {
  auth: {
    /** A4 */
    me: () => qo<CurrentUserDto>(['auth', 'me'], () => api.get('/auth/me'), 5 * MIN),
  },

  periods: {
    /** P2 */
    current: () => qo<PayPeriodDto>(['periods', 'current'], () => api.get('/pay-periods/current'), 5 * MIN),
    /** P1 */
    list: (f: Filters = {}) =>
      qo<Paginated<PayPeriodDto>>(['periods', 'list', f], () => api.get('/pay-periods', f), 5 * MIN),
    /** P1a — the same list under `job.read`, for Attendance and Meal Allowance, whose users
     *  hold no `payroll.read`. Under `['periods']` so period mutations refresh it too. */
    lookup: (f: Filters = {}) =>
      qo<Paginated<PayPeriodDto>>(['periods', 'lookup', f], () => api.get('/pay-periods/lookup', f), 5 * MIN),
    /** P10 */
    readiness: (id: string) =>
      qo<PeriodReadinessDto>(['periods', 'readiness', id], () => api.get(`/pay-periods/${id}/readiness`), 30_000),
    /**
     * FR-6.1 — the monthly cut-off and the periods it will generate next.
     *
     * Under the `['periods']` prefix on purpose: every period mutation already
     * invalidates that prefix, and creating a period changes what this preview
     * says, so sharing the prefix keeps the two in step for free.
     */
    boundary: () =>
      qo<PeriodBoundaryDto>(['periods', 'boundary'], () => api.get('/pay-periods/boundary'), MIN),
  },

  nav: {
    /** Z6 — one call, every count scoped to the caller's permissions. */
    counts: () => qo<NavCountsDto>(['nav', 'counts'], () => api.get('/meta/nav-counts'), MIN),
  },

  sheets: {
    /** J1 */
    list: (f: Filters) =>
      qo<Paginated<JobDayListItemDto>>(['sheets', 'list', f], () => api.get('/job-days', f), 30_000),
    /** J2 */
    summary: (f: Filters) =>
      qo<JobDaySummaryDto>(['sheets', 'summary', f], () => api.get('/job-days/summary', f), MIN),
    /** J3 — staleTime 0: the day sheet is the live aggregate. */
    detail: (p: { date: string; teamId: string; shift?: string }) =>
      qo<DaySheetDto>(
        ['job-day', p.date, p.teamId, p.shift ?? 'DAY'],
        () => api.get(`/job-days/${p.date}/${p.teamId}`, { shift: p.shift ?? 'DAY' }),
        0,
      ),
    /** J26 */
    audit: (p: { date: string; teamId: string }) =>
      qo<Paginated<AuditEntryDto>>(
        ['job-day', 'audit', p],
        () => api.get(`/job-days/${p.date}/${p.teamId}/audit`),
        30_000,
      ),
  },

  workTypes: {
    /** R2 — `asOf` is the job day's workDate, NEVER today. */
    lookup: (p: WorkTypeLookupQuery & Filters) =>
      qo<WorkTypeLookupItemDto[]>(['work-types', 'lookup', p], () => api.get('/work-types/lookup', p), MIN),
  },

  rates: {
    /** R1 */
    workTypes: (f: Filters = {}) =>
      qo<Paginated<WorkTypeListItemDto>>(['rates', 'work-types', f], () => api.get('/work-types', f), MIN),
    /** R1, every page — the rate card and Work Types screens show the whole register. */
    workTypesAll: (f: Filters = {}) =>
      qo<Paginated<WorkTypeListItemDto>>(
        ['rates', 'work-types', 'all', f],
        () => allPages<Paginated<WorkTypeListItemDto>>('/work-types', f),
        MIN,
      ),
    /** R7 */
    history: (id: string, f: Filters = {}) =>
      qo<Paginated<RateHistoryEntryDto>>(
        ['rates', 'history', id, f],
        () => api.get(`/work-types/${id}/rates`, f),
        MIN,
      ),
    /**
     * R18 — what setting this rate would do to the job lines already entered (ADR-0020). A
     * POST, but a read: the server runs R16's own transaction and rolls it back. Under
     * `['rates']`, so `invalidateRates` refreshes it after any rate write.
     */
    setPreview: (id: string, body: SetRateRequest) =>
      qo<RateRepriceSummaryDto>(
        ['rates', 'set-preview', id, body],
        () => api.post(`/work-types/${id}/rates/preview`, body),
        0,
      ),
    /** R19 with `dryRun` — the entered lines the rate card now prices differently. */
    reapplyPreview: (id: string, kind: RateKind) =>
      qo<RateRepriceSummaryDto>(
        ['rates', 'reapply-preview', id, kind],
        () => api.post(`/work-types/${id}/rates/reapply`, { kind, dryRun: true }),
        0,
      ),
    /**
     * R20 with `dryRun` (ADR-0021) — what correcting one window would do, refusals included.
     * A PATCH, but a read: the server runs the correction and rolls it back.
     */
    correctPreview: (id: string, rateId: string, body: { amount?: string; validFrom?: string }) =>
      qo<RateCorrectionResultDto>(
        ['rates', 'correct-preview', id, rateId, body],
        () =>
          api.patch(`/work-types/${id}/rates/${rateId}`, {
            ...body,
            repriceEnteredLines: true,
            dryRun: true,
          }),
        0,
      ),
    /** R17 with `dryRun` (ADR-0021) — what deleting one window would do. */
    deletePreview: (id: string, rateId: string) =>
      qo<RateCorrectionResultDto>(
        ['rates', 'delete-preview', id, rateId],
        () =>
          api.del(`/work-types/${id}/rates/${rateId}`, {
            query: { repriceEnteredLines: true, dryRun: true },
          }),
        0,
      ),
  },

  workers: {
    /** W1 */
    list: (f: Filters = {}) =>
      qo<Paginated<WorkerListItemDto>>(['workers', 'list', f], () => api.get('/workers', f), MIN),
    /** W1, every page (`allPages`) — for pickers, facets and id → name maps, never a table. */
    all: (f: Filters = {}) =>
      qo<Paginated<WorkerListItemDto>>(
        ['workers', 'all', f],
        () => allPages<Paginated<WorkerListItemDto>>('/workers', f),
        MIN,
      ),
    /** W6 */
    profile: (id: string, p: Filters = {}) =>
      qo<WorkerProfileDto>(['workers', 'profile', id, p], () => api.get(`/workers/${id}/profile`, p), MIN),
    /** D1 */
    documents: (id: string) =>
      qo<WorkerDocumentDto[]>(['workers', 'documents', id], () => api.get(`/workers/${id}/documents`), MIN),
    /**
     * W10 — every engagement this worker has ever held, effective-dated
     * (FR-5.4). An ARRAY, not `Paginated`: the endpoint returns the whole
     * history because a worker has a handful of windows, not a page of them.
     *
     * Distinct from `qk.casual.engagements`, which is the plant-wide C1 list
     * filtered by `asOf`. This one is the worker's own timeline, ended rows
     * included, and it is what the profile's pay-scheme panel renders.
     */
    paySchemes: (id: string) =>
      qo<EngagementDto[]>(['workers', 'pay-schemes', id], () => api.get(`/workers/${id}/pay-schemes`), MIN),
    /**
     * W37 `GET /workers/{id}/photo` — the photo BYTES, not a DTO, so this is
     * the one `qk` entry whose `queryFn` returns a `Blob` rather than JSON
     * (ADR-0015).
     *
     * Keyed on `(workerId, photoUpdatedAt)`, not `workerId` alone: a
     * re-upload changes `photoUpdatedAt` on the worker row, which is exactly
     * what makes this a NEW cache key rather than a stale blob served under
     * an unchanged one — the version IS the value W37 sends back as `ETag`.
     * Callers pass `photoUpdatedAt` through `useWorkerPhoto` below rather
     * than calling this directly, since a `null` version (no photo yet) must
     * not be fetched at all.
     *
     * `staleTime: Infinity` because the key already encodes the version —
     * nothing this query returns can go stale under an unchanged key.
     * `gcTime` is finite so a long session does not pin every photo ever
     * glanced at in memory as a `Blob` forever; an hour matches how long a
     * clerk might reasonably work one screen.
     *
     * The key deliberately does NOT start with `'workers'`. Every worker
     * mutation invalidates that prefix (`-EditWorkerDrawer.tsx`, the photo
     * modal), and a photo under it would be marked stale and refetched for
     * every visible row on a phone-number edit — for nothing, since the key
     * already carries the version. A new photo arrives as a new
     * `photoUpdatedAt` on the refetched worker rows, i.e. a new key.
     */
    photo: (workerId: string, photoUpdatedAt: string) =>
      queryOptions({
        queryKey: ['worker-photo', workerId, photoUpdatedAt] as const,
        queryFn: () => fetchBlob(`/workers/${workerId}/photo`),
        staleTime: Infinity,
        gcTime: 60 * MIN,
      }),
  },

  teams: {
    /** W13 */
    lookup: (p: Filters = {}) =>
      qo<TeamRefDto[]>(['teams', 'lookup', p], () => api.get('/teams/lookup', p), 10 * MIN),
    /**
     * W12 — the full register, `TeamDto` and therefore `isWildcard`.
     *
     * Distinct from `lookup` on purpose. W13 exists to fill rostering pickers
     * and so excludes the `*` sentinel (`teams.service.ts` filters
     * `isWildcard: false`) — you cannot put a worker on `*`. But an OVERRIDING
     * rate legitimately keys to it: D-6 makes `*` the "all teams" key, and the
     * seed's own leader rates use it. A screen that has to offer "All teams"
     * therefore needs this read, not `lookup`, and can find the sentinel by its
     * flag instead of hardcoding the fixed uuid.
     */
    all: (p: Filters = {}) =>
      qo<Paginated<TeamDto>>(['teams', 'all', p], () => api.get('/teams', p), 10 * MIN),
    /** W22 */
    roster: (p: { teamId: string; asOf: string }) =>
      qo<RosterEntryDto[]>(
        ['teams', 'roster', p],
        () => api.get(`/teams/${p.teamId}/roster`, { asOf: p.asOf }),
        30_000,
      ),
  },

  /**
   * ADR-0016 — J33 / J37 (`/products`, `/customers`). One factory for both lists, keyed by
   * kind so a product write never invalidates the customer cache. MASTER DATA, so 10 minutes
   * like `agents`; every J34–J40 write invalidates `['jobMaster', kind]` and the day sheet
   * re-reads its own refs off the sheet, never from here.
   */
  jobMaster: {
    list: (kind: JobMasterKind, f: Filters = {}) =>
      qo<Paginated<JobMasterDto>>(
        ['jobMaster', kind, f],
        () => api.get(kind === 'product' ? '/products' : '/customers', f),
        10 * MIN,
      ),
    /** J33 / J37, every page — filter dropdowns that must offer every name. */
    all: (kind: JobMasterKind, f: Filters = {}) =>
      qo<Paginated<JobMasterDto>>(
        ['jobMaster', kind, 'all', f],
        () => allPages<Paginated<JobMasterDto>>(kind === 'product' ? '/products' : '/customers', f),
        10 * MIN,
      ),
  },

  /** ADR-0016 — J41, the job-line search across papers. Short-lived: lines move all day. */
  jobLines: {
    search: (f: Filters) =>
      qo<JobLineSearchResultDto>(['jobLines', 'search', f], () => api.get('/job-lines', f), 30_000),
    /** J50 — production lines on file (migration 0065). Under `['jobLines']`, so a day sheet's
     *  `setSheet` invalidates it with the search. */
    productionLines: () =>
      qo<string[]>(['jobLines', 'productionLines'], () => api.get('/job-lines/production-lines'), 60_000),
  },

  /**
   * ADR-0019 — J43 / J45, the daily and monthly job reports. `teamIds` travels as ONE
   * comma-separated value, never as a repeated key: Express's qs parser stops returning an
   * array after 20 repeats (`job-reports/job-report.dto.ts`), and "every team but two" is the
   * ordinary pick. 30 s like J41 — a report is a read of papers that may still be being keyed,
   * and Generate always refetches anyway.
   */
  jobReports: {
    daily: (f: { date: string; teamIds?: string }) =>
      qo<JobReportDto>(['jobReports', 'daily', f], () => api.get('/job-reports/daily', f), 30_000),
    monthly: (f: { periodId: string; teamIds?: string }) =>
      qo<JobReportDto>(['jobReports', 'monthly', f], () => api.get('/job-reports/monthly', f), 30_000),
  },

  /** W25 */
  agents: {
    list: (f: Filters = {}) => qo<Paginated<AgentDto>>(['agents', f], () => api.get('/agents', f), 10 * MIN),
    /** W27, every page — the agent pickers and the register's agent facet. */
    all: (f: Filters = {}) =>
      qo<Paginated<AgentDto>>(
        ['agents', 'all', f],
        () => allPages<Paginated<AgentDto>>('/agents', f),
        10 * MIN,
      ),
  },

  roster: {
    /** W2 — the add-worker-from-another-team picker. */
    available: (p: Filters) =>
      qo<AvailableWorkerDto[]>(['workers', 'available', p], () => api.get('/workers/available', p), 30_000),
  },

  documents: {
    /** D3 */
    dashboard: (f: Filters = {}) =>
      qo<DocumentExpiryDashboardDto>(
        ['documents', 'dashboard', f],
        () => api.get('/documents/expiry-dashboard', f),
        5 * MIN,
      ),
    /** D12 */
    band: (f: Filters) =>
      qo<Paginated<WorkerDocumentDto>>(['documents', 'band', f], () => api.get('/documents', f), 5 * MIN),
    /** D12, every page — a band shown in full (the EXPIRED band alone was 173 of 200 live). */
    bandAll: (f: Filters) =>
      qo<Paginated<WorkerDocumentDto>>(
        ['documents', 'band', 'all', f],
        () => allPages<Paginated<WorkerDocumentDto>>('/documents', f),
        5 * MIN,
      ),
    /** T10 — the only source of the MISSING state. */
    compliance: (f: Filters = {}) =>
      qo<DocumentComplianceReportDto>(
        ['reports', 'compliance', f],
        () => api.get('/reports/document-compliance', f),
        5 * MIN,
      ),
    /** T10, every page — the MISSING list is built from the rows, so page 1 is not enough. */
    complianceAll: (f: Filters = {}) =>
      qo<DocumentComplianceReportDto>(
        ['reports', 'compliance', 'all', f],
        () => allPages<DocumentComplianceReportDto>('/reports/document-compliance', f),
        5 * MIN,
      ),
  },

  casual: {
    /**
     * F20 `GET /config/casual-slots` — the casual pay master, live rows only.
     *
     * MASTER DATA, so 10 minutes like `teams.lookup` and the other `config`
     * lookups. It is keyed under `['casual', …]` rather than `['config', …]`
     * deliberately: `invalidateConfig` in `admin/-shared/admin-api.ts` already
     * invalidates BOTH families, so an F20 write is reflected immediately
     * either way, and grouping it with the engagements that reference it keeps
     * one screen's cache in one subtree.
     *
     * Every row carries `hourlyRate`, derived server-side by R-5. NEVER divide
     * `defaultDailyRate` by `defaultHoursPerDay` in a component — that is a
     * second implementation of the rule and it will drift.
     */
    slots: () =>
      qo<CasualSlotDto[]>(['casual', 'slots'], () => api.get('/config/casual-slots'), 10 * MIN),
    /**
     * C1 `GET /casual/engagements` — PAGINATED.
     *
     * Takes the whole filter bag (`workerId`, `payScheme`, `asOf`,
     * `includeEnded`, plus pagination), not a bare `asOf` string as it once
     * did: §8.1 declares all four and the callers that need `payScheme` were
     * filtering client-side over a page of results, which silently loses rows
     * once the plant outgrows one page.
     *
     * 60 s, not the 5 minutes it used to hold — an engagement is a workforce
     * fact a clerk edits and expects to see move, not master data.
     */
    engagements: (f: Filters = {}) =>
      qo<Paginated<EngagementDto>>(
        ['casual', 'engagements', f],
        () => api.get('/casual/engagements', f),
        MIN,
      ),
    /** C1, every page — the casual grid lists every casual worker in the period. */
    engagementsAll: (f: Filters = {}) =>
      qo<Paginated<EngagementDto>>(
        ['casual', 'engagements', 'all', f],
        () => allPages<Paginated<EngagementDto>>('/casual/engagements', f),
        MIN,
      ),
    /**
     * C12 `GET /casual/timesheets/periods` — every pay period, newest first,
     * in the picker shape. It is the casual screen's period list because P1
     * needs `payroll.read`, which CLERK and HR do not hold.
     *
     * Keyed under `['periods']` ON PURPOSE, not `['casual']`: a close, lock or
     * reopen from Admin → Pay Periods invalidates that prefix and the picker's
     * status must move with it, while a casual write never changes a period
     * row and need not reach this key.
     */
    periods: () =>
      qo<PayPeriodRefDto[]>(
        ['periods', 'casual-lookup'],
        () => api.get('/casual/timesheets/periods'),
        5 * MIN,
      ),
    /** C7 — the sheet: one worker × one pay period. */
    period: (p: { workerId: string; periodId: string }) =>
      qo<CasualPeriodDto>(
        ['casual', 'period', p],
        () => api.get('/casual/timesheets/period', p),
        30_000,
      ),
  },

  payroll: {
    /** P11 */
    runs: (f: Filters = {}) =>
      qo<Paginated<PayrollRunListItemDto>>(['payroll', 'runs', f], () => api.get('/payroll/runs', f), 30_000),
    /**
     * T14 — the same list under `report.read`, for the report screens' run picker (UAT PAY-048:
     * Supervisor and HR hold `report.read` but not `payroll.read`, so P11 403'd them). Keyed
     * UNDER `['payroll', 'runs']` on purpose, so every invalidation that refreshes P11 after a
     * compute, lock or void refreshes this too.
     */
    reportRuns: (f: Filters = {}) =>
      qo<Paginated<PayrollRunListItemDto>>(
        ['payroll', 'runs', 'for-reports', f],
        () => api.get('/reports/runs', f),
        30_000,
      ),
    /** P13 */
    run: (id: string) =>
      qo<PayrollRunDto>(['payroll', 'run', id], () => api.get(`/payroll/runs/${id}`), 30_000),
    /** P15 — staleTime 0, polled while RUNNING. */
    progress: (id: string) =>
      qo<JobTicketDto>(['payroll', 'progress', id], () => api.get(`/payroll/runs/${id}/progress`), 0),
    /** P20 */
    lines: (p: { runId: string } & Filters) =>
      qo<Paginated<PayrollLineListItemDto>>(
        ['payroll', 'lines', p],
        () => api.get(`/payroll/runs/${p.runId}/lines`, p),
        30_000,
      ),
    /** P21 */
    payslip: (p: { runId: string; workerId: string }) =>
      qo<PayslipDto>(
        ['payroll', 'payslip', p],
        () => api.get(`/payroll/runs/${p.runId}/lines/${p.workerId}`),
        30_000,
      ),
    /** P22 — every wage traceable to the job paper (G5). */
    sources: (p: { runId: string; workerId: string } & Filters) =>
      qo<Paginated<PayrollLineSourceDto>>(
        ['payroll', 'sources', p],
        () => api.get(`/payroll/runs/${p.runId}/lines/${p.workerId}/sources`, p),
        MIN,
      ),
    /** P31 */
    disbursements: (p: { runId: string } & Filters) =>
      qo<Paginated<DisbursementDto>>(
        ['payroll', 'disb', p],
        () => api.get(`/payroll/runs/${p.runId}/disbursements`, p),
        30_000,
      ),
    /** X2 */
    exports: (p: { runId: string } & Filters) =>
      qo<ExportBatchDto[]>(['payroll', 'exports', p], () => api.get(`/payroll/runs/${p.runId}/exports`, p), 30_000),
  },

  reports: {
    /** T5 */
    reconciliation: (p: Filters) =>
      qo<ReconciliationReportDto>(['reports', 'recon', p], () => api.get('/reports/reconciliation', p), 30_000),
    /** T7 */
    variance: (p: Filters) =>
      qo<VarianceReportDto>(['reports', 'variance', p], () => api.get('/reports/variance', p), 30_000),
    /** T8 */
    register: (p: Filters) =>
      qo<PayrollRegisterDto>(['reports', 'register', p], () => api.get('/reports/payroll-register', p), MIN),
    /** T12 — FR-7.5, the daily earnings grid (ADR-0008). */
    dailyEarnings: (p: Filters) =>
      qo<DailyEarningsReportDto>(['reports', 'daily-earnings', p], () => api.get('/reports/daily-earnings', p), MIN),
    /** T2 */
    baselines: (p: Filters = {}) =>
      qo<Paginated<ReconciliationBaselineDto>>(
        ['reports', 'baselines', p],
        () => api.get('/reconciliation/baselines', p),
        MIN,
      ),
  },

  /**
   * ADR-0006 — the medical allowance.
   *
   * Its own namespace and NOT under `workers`, because the register reads across the whole
   * company and a `['workers', …]` key would drag it into every blanket worker invalidation —
   * and, worse, would not be invalidated by a claim recorded from the register screen.
   *
   * A SHORT staleTime, unlike the config reads below: this is a running balance an admin edits
   * and then reads back on the same screen, so a minute of staleness would show a clerk the
   * total from before their own claim.
   */
  medical: {
    /** One worker's entitlement, usage and claims for one calendar year. */
    summary: (workerId: string, year: number) =>
      qo<WorkerMedicalSummaryDto>(
        ['medical', 'summary', workerId, year],
        () => api.get('/medical-claims/summary', { workerId, year }),
        30_000,
      ),
    /** The company-wide register. `claimedTotal` is over the whole filter, not the page. */
    list: (f: Filters = {}) =>
      qo<Paginated<MedicalClaimDto> & { claimedTotal: string; year: number }>(
        ['medical', 'list', f],
        () => api.get('/medical-claims', f),
        30_000,
      ),
  },

  /**
   * ADR-0018 — leave balances: a yearly day allowance on an exception code.
   *
   * Its own namespace for the medical reason above: a `['workers', …]` key would be thrown away
   * by every unrelated worker edit, and would NOT be thrown away by the thing that actually
   * moves it — a day marked on `/attendance` or the crew card. `invalidateAttendance` drops
   * `['leave']` for exactly that reason. Short staleness, because an admin changes a worker's
   * number and reads the balance back on the same panel.
   */
  leave: {
    /** W42 — one worker's balance on every counted code for one calendar year. */
    worker: (workerId: string, year: number) =>
      qo<WorkerLeaveBalancesDto>(
        ['leave', 'worker', workerId, year],
        () => api.get(`/workers/${workerId}/leave-balances`, { year }),
        30_000,
      ),
  },

  /**
   * ADR-0007. The attendance board for one pay period.
   *
   * Its own key subtree rather than a child of `['jobs']`, even though it reads the same
   * `job_day_roster` rows the day sheet does: marking an event here invalidates BOTH, and a
   * shared prefix would make "invalidate the board" also throw away every loaded day sheet.
   * `-attendance-api.ts` invalidates the two explicitly instead.
   *
   * Short staleness, because a clerk marks and reads back on the same screen and the penalty
   * preview beside the grid must move with the marking.
   */
  attendance: {
    board: (f: Filters = {}) =>
      qo<AttendanceBoardDto>(['attendance', 'board', f], () => api.get('/attendance', f), 15_000),
  },

  /**
   * ADR-0011. The canteen board for one pay period — who was granted a day or night meal on
   * the job sheets, and who came to the canteen.
   *
   * Its own subtree for the same reason `attendance` has one: a tick invalidates this board
   * and `['payroll']` (a night meal's cash moves), never the day sheets — the tick lives in
   * `canteen_attendance` and the crew card shows grants, not ticks. Short staleness because the
   * office ticks and reads the Cash column back on the same screen.
   */
  canteen: {
    board: (f: Filters = {}) =>
      qo<CanteenBoardDto>(['canteen', 'board', f], () => api.get('/canteen', f), 15_000),
  },

  config: {
    /**
     * F18. Read by the Letterhead admin tab and by the payslip card's
     * print-only header (FR-6.8) — the browser-print path needs the employer
     * identity that the server-rendered PDF resolves for itself.
     */
    payingEntities: () =>
      qo<PayingEntityDto[]>(['config', 'paying-entities'], () => api.get('/config/paying-entities'), 10 * MIN),
    /** F8 */
    exceptionCodes: (p: Filters = {}) =>
      qo<ExceptionCodeRow[]>(['config', 'exception-codes', p], () => api.get('/config/exception-codes', p), 10 * MIN),
    /** F10 */
    timeMultipliers: (p: Filters = {}) =>
      qo<TimeMultiplierDto[]>(['config', 'time-multipliers', p], () => api.get('/config/time-multipliers', p), 10 * MIN),
    /**
     * F37 — every unit of measure, retired ones included (ADR-0017). Read far beyond the Units
     * tab: the job sheet, the rate card and the job-lines search print each unit's short
     * label from it, through `lib/units.ts`'s `useUnitLabels` — which is why it is one
     * parameterless key, shared by all of them.
     */
    units: () =>
      qo<UnitOfMeasureDto[]>(['config', 'units'], () => api.get('/config/units'), 10 * MIN),
    /** F12 */
    payComponentTypes: () =>
      qo<PayComponentTypeDto[]>(['config', 'pay-component-types'], () => api.get('/config/pay-component-types'), 10 * MIN),
    /** F5 */
    payrollPolicy: (p: Filters = {}) =>
      qo<PayrollPolicyDto>(['config', 'payroll-policy', p], () => api.get('/config/payroll-policy', p), 10 * MIN),
    /**
     * F24a. `{ from, to }`, both inclusive and both required.
     *
     * Typed from the shared DTO since migration 0015 gave the calendar a table
     * and `PublicHolidayDto` a home in `@cropmate/shared` — it used to declare
     * an inline `{date, name}` here and silently drop `scope`.
     *
     * An hour of staleTime is right for the casual timesheet, which reads this
     * for day-header decoration. The admin panel writes through
     * `invalidateConfig`, so an edit is visible immediately regardless.
     */
    publicHolidays: (p: Filters) =>
      qo<PublicHolidayDto[]>(
        ['config', 'public-holidays', p],
        () => api.get('/config/public-holidays', p),
        60 * MIN,
      ),
    /**
     * F14 — `?asOf=&scheme=`. The OQ-1 sign-off agenda.
     *
     * A SHORT staleTime on purpose, unlike its neighbours. Every other config
     * read here is decoration that can lag a minute harmlessly; this one drives
     * a decision the client makes once and a payroll LOCK then depends on
     * (`E_RUN_UNCONFIRMED_CONFIG`). Thirty seconds keeps a rule that a second
     * administrator confirmed from still reading "not confirmed" on this
     * screen while someone tries to lock against it.
     */
    statutoryRules: (p: Filters = {}) =>
      qo<StatutoryRuleDto[]>(
        ['config', 'statutory-rules', p],
        () => api.get('/config/statutory-rules', p),
        30_000,
      ),
    /** F16 — `?asOf=`. Answers OQ-2; ships as mode NONE, financially inert. */
    absencePenaltyRules: (p: Filters = {}) =>
      qo<AbsencePenaltyRuleDto[]>(
        ['config', 'absence-penalty-rules', p],
        () => api.get('/config/absence-penalty-rules', p),
        10 * MIN,
      ),
    /** F21 */
    entries: (p: Filters = {}) =>
      qo<AppConfigEntryDto[]>(['config', 'entries', p], () => api.get('/config/entries', p), 10 * MIN),
  },

  /** F1 */
  audit: {
    list: (f: Filters = {}) =>
      qo<Paginated<AuditEntryDto>>(['audit', 'list', f], () => api.get('/audit-log', f), 30_000),
  },

  admin: {
    /** F27 */
    users: (f: Filters = {}) =>
      qo<Paginated<UserDto>>(['admin', 'users', f], () => api.get('/users', f), MIN),
    /** F34 */
    roles: () => qo<RoleDto[]>(['admin', 'roles'], () => api.get('/roles'), 60 * MIN),
  },

  /** Z1 — async work is POLLED, never awaited. 1 s while RUNNING, 5 s after 30 s. */
  jobs: {
    ticket: (id: string) => qo<JobTicketDto>(['jobs', id], () => api.get(`/jobs/${id}`), 0),
  },
} as const;

/**
 * W37 — the cached photo `Blob` for one worker, or `undefined` while there is
 * none to fetch (ADR-0015).
 *
 * `enabled: false` whenever `photoUpdatedAt` is `null`: a worker with no
 * stored photo has nothing at `/workers/{id}/photo` — W37 answers 404 — so a
 * caller passing `photoUpdatedAt` straight from `WorkerListItemDto` /
 * `WorkerDto` never needs its own null guard before calling this hook. This
 * is a plain wrapper over `useQuery`, not a bespoke fetcher, so callers keep
 * the usual `data` / `isPending` / `isError` shape — `WorkerAvatar.tsx`'s
 * `useWorkerPhotoUrl` is the only place that turns the `Blob` into something
 * paintable, and it is what both `<WorkerAvatar>` and the day-sheet's photo
 * preview modal (`WorkerPhotoPreviewModal.tsx`) call, so the two never issue a second W37 request
 * for the same `(workerId, photoUpdatedAt)`.
 */
export function useWorkerPhoto(workerId: string, photoUpdatedAt: string | null) {
  return useQuery({
    ...qk.workers.photo(workerId, photoUpdatedAt ?? ''),
    enabled: photoUpdatedAt !== null,
  });
}
